import type { ImageEvaluation } from "@/lib/api/agents"
import type { VehicleSide } from "@/lib/api/types"
import type { PlannedShot, ShotVerdict } from "./types"

// Consumer-walk verdict: the AI's photo check → one plain instruction.
// (The staff wizard keeps derive-verdict.ts; this one knows corners and shot
// kinds.)
//
//   1. Wrong side — only for whole-car / closer shots, with tolerance: the
//      front-left corner accepts the front, the left side, the front-left
//      side… it only objects to the other end or the other side.
//   2. Dark, blurry — any shot.
//   3. Too close — whole-car shots ONLY. A close-up is supposed to be close;
//      it must never hear "step back".
//   4. Score floor (lower for close-ups — texture, not framing, matters).

type Evaluation = Pick<ImageEvaluation, "qualified" | "qualityScore" | "qualityIssues" | "side">

const DARK = ["dark", "lighting", "underexpos", "shadow"]
const BLUR = ["blur", "shake", "out of focus", "motion"]
const TOO_CLOSE = ["too close", "stand back", "too near", "not fully in frame", "cut off", "cropped"]

interface Axes {
  end: "FRONT" | "REAR" | null
  lr: "LEFT" | "RIGHT" | null
}

export function sideAxes(side: VehicleSide | string | null | undefined): Axes {
  const s = String(side ?? "")
  return {
    end: s.startsWith("FRONT") ? "FRONT" : s.startsWith("REAR") ? "REAR" : null,
    lr: s.includes("LEFT") ? "LEFT" : s.includes("RIGHT") ? "RIGHT" : null,
  }
}

// Whole-car shot from a corner: the detection may be any face that corner
// sees (unknown axes are fine), but never the other end or the other side.
function cornerAccepts(planned: VehicleSide, detected: VehicleSide | string): boolean {
  const p = sideAxes(planned)
  const d = sideAxes(detected)
  return (!d.end || !p.end || d.end === p.end) && (!d.lr || !p.lr || d.lr === p.lr)
}

// Closer shot: must share the front/rear half or the left/right side.
function closerAccepts(planned: VehicleSide, detected: VehicleSide | string): boolean {
  const p = sideAxes(planned)
  const d = sideAxes(detected)
  if ((!p.end && !p.lr) || (!d.end && !d.lr)) return true
  return (!!p.end && p.end === d.end) || (!!p.lr && p.lr === d.lr)
}

export function sideMatchesPlan(shot: PlannedShot, detected: VehicleSide | string | null): boolean {
  if (!shot.side || !detected) return true
  if (shot.kind === "wide") return cornerAccepts(shot.side, detected)
  if (shot.kind === "closer") return closerAccepts(shot.side, detected)
  return true
}

function has(text: string, needles: string[]): boolean {
  return needles.some((n) => text.includes(n))
}

export function consumerVerdict(
  evaluation: Evaluation,
  shot: PlannedShot | null,
): ShotVerdict {
  const kind = shot?.kind ?? "closeup"
  if (shot && !sideMatchesPlan(shot, evaluation.side)) return "wrong-side"

  const issues = (evaluation.qualityIssues ?? []).join(" ").toLowerCase()
  if (has(issues, DARK)) return "dark"
  if (has(issues, BLUR)) return "blurry"
  if (kind === "wide" && has(issues, TOO_CLOSE)) return "too-close"

  const floor = kind === "closeup" || kind === "sticker" ? 50 : 60
  if (!evaluation.qualified || evaluation.qualityScore < floor) return "low-quality"
  return "good"
}
